import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useCare, useLiveCare, errorText } from './CareContext';
import { CareFinancialActions } from './CareFinancialActions';
import { CareReceiptButton } from './CareReceiptButton';

export const money = (minor, currency = 'GHS') => `${currency} ${(Number(minor || 0) / 100).toFixed(2)}`;

const QuoteCard = ({ q, patientMode, answering, note, busy, onOpenAnswer, onNoteChange, onAnswer }) => (
  <article key={q.quote_id} className="care-record" data-testid={`care-quote-record-${q.quote_id}`}>
    <div className="flex flex-wrap justify-between gap-2">
      <strong>{money(q.total_minor, q.currency)}</strong>
      <span className="text-sm" data-testid={`care-quote-state-${q.quote_id}`}>
        {q.status} · expires {new Date(q.expires_at).toLocaleString()}
      </span>
    </div>
    <p className="text-xs font-mono break-all">{q.request_id}</p>
    <ul className="my-3 text-sm" data-testid={`care-quote-lines-${q.quote_id}`}>
      {q.items.map(i => <li key={i.product_id}>{i.name || i.product_id} · {i.quantity} × {money(i.unit_minor, q.currency)}</li>)}
    </ul>
    {q.note && <p className="whitespace-pre-wrap break-words text-sm" data-testid={`care-quote-private-${q.quote_id}`}>{q.note}</p>}
    {q.patient_answer && (
      <div className="mt-3 border-t pt-3">
        <p className="text-xs text-muted-foreground">Patient response</p>
        <p className="whitespace-pre-wrap break-words text-sm" data-testid={`care-quote-patient-answer-${q.quote_id}`}>
          {q.patient_answer.note || q.patient_answer.action}
        </p>
      </div>
    )}
    {patientMode && q.status === 'awaiting_patient' && new Date(q.expires_at) > new Date() && (
      <Button className="mt-3" variant="outline" onClick={() => onOpenAnswer(q.quote_id)} data-testid={`care-answer-open-${q.quote_id}`}>
        Respond to quote
      </Button>
    )}
    {answering === q.quote_id && (
      <div className="space-y-3 mt-3">
        <Textarea value={note} onChange={e => onNoteChange(e.target.value)} placeholder="Response or question" data-testid="care-quote-answer-note" />
        <div className="flex flex-wrap gap-2">
          {['approve', 'decline', 'question'].map(action => (
            <Button
              key={action}
              disabled={busy || (action === 'question' && !note.trim())}
              onClick={() => onAnswer(q, action)}
              data-testid={`care-quote-answer-${action}`}
            >
              {action === 'approve' ? 'Approve exact quote' : action === 'decline' ? 'Decline' : 'Ask a question'}
            </Button>
          ))}
        </div>
      </div>
    )}
  </article>
);

const BillCard = ({ b, patientMode, busy, amount, date, onAmountChange, onDateChange, onPay, onLayaway, onCancelRequest, onAgreeDueDate, onRecheck, onRefresh, onVerifyPayment, payments }) => (
  <article className="care-record space-y-3" key={b.bill_id} data-testid={`care-bill-${b.bill_id}`}>
    <div className="flex flex-wrap justify-between gap-2">
      <span className="care-origin">Online · {b.request_type.toUpperCase()}</span>
      <span data-testid={`care-bill-status-${b.bill_id}`}>{b.status}</span>
    </div>
    <div className="care-status-grid" data-testid={`care-bill-balance-${b.bill_id}`}>
      <span>Bill: <b>{money(b.total_minor, b.currency)}</b></span>
      <span>Paid: <b>{money(b.paid_minor, b.currency)}</b></span>
      <span>Remaining: <b>{money(b.remaining_minor, b.currency)}</b></span>
    </div>
    {b.resolution && <p role="alert" className="care-warning" data-testid={`care-bill-resolution-${b.bill_id}`}>{b.resolution}</p>}
    {b.layaway && (
      <p className="text-sm" data-testid={`care-layaway-${b.bill_id}`}>
        Layaway · due {new Date(b.due_at).toLocaleDateString()} · {b.due_date_status}. No stock held between
        instalments; no interest, fees or automatic forfeiture.
      </p>
    )}
    <ul className="text-sm" data-testid={`care-payment-history-${b.bill_id}`}>
      {b.payments.map(p => <li key={p.reference} className="break-all">{new Date(p.at).toLocaleDateString()} · {p.method} · {money(p.amount_minor, b.currency)}</li>)}
    </ul>
    <CareReceiptButton saleId={b.sale_id} context={b.bill_id} />

    {patientMode && !['collected', 'cancelled'].includes(b.status) && (
      <>
        {!b.layaway && (
          <div className="care-form-grid">
            <label className="care-label">
              Proposed layaway due date
              <Input type="datetime-local" value={date || ''} onChange={e => onDateChange(e.target.value)} data-testid={`care-layaway-date-${b.bill_id}`} />
            </label>
            <Button variant="outline" disabled={busy || !date} onClick={onLayaway} data-testid={`care-layaway-request-${b.bill_id}`}>
              Request instalments
            </Button>
          </div>
        )}
        <p className="text-sm" data-testid={`care-payment-availability-${b.bill_id}`}>
          {b.online_available
            ? 'Online payment available; stock and eligibility are rechecked before final payment.'
            : 'Online merchant is unconfigured. Payment at collection is available through the pharmacy POS.'}
        </p>
        {b.online_available && b.remaining_minor > 0 && (
          <div className="flex gap-2">
            <Input
              type="number" min="0.01" step="0.01" aria-label="Payment amount"
              value={amount ?? b.remaining_minor / 100}
              onChange={e => onAmountChange(e.target.value)}
              data-testid={`care-pay-amount-${b.bill_id}`}
            />
            <Button disabled={busy || !!b.resolution || !b.approval_current} onClick={onPay} data-testid={`care-pay-${b.bill_id}`}>Pay</Button>
          </div>
        )}
        <Button variant="outline" disabled={busy} onClick={onCancelRequest} data-testid={`care-cancel-request-${b.bill_id}`}>
          Request cancellation / refund
        </Button>
      </>
    )}

    {!patientMode && (
      <div className="flex flex-wrap gap-2">
        {b.due_date_status === 'awaiting_staff_agreement' && (
          <Button disabled={busy} onClick={onAgreeDueDate} data-testid={`care-due-agree-${b.bill_id}`}>Agree due date</Button>
        )}
        {b.status !== 'collected' && (
          <Button asChild><Link to={'/pos?care_bill=' + b.bill_id} data-testid={`care-bill-pos-${b.bill_id}`}>Open in POS</Link></Button>
        )}
        {b.resolution && (
          <Button variant="outline" disabled={busy} onClick={onRecheck} data-testid={`care-bill-recheck-${b.bill_id}`}>Recheck eligibility</Button>
        )}
      </div>
    )}
    {!patientMode && <CareFinancialActions bill={b} onChange={onRefresh} />}
    {payments.filter(p => p.bill_id === b.bill_id && p.status !== 'paid').map(p => (
      <Button key={p.reference} variant="outline" disabled={busy} onClick={() => onVerifyPayment(p)} data-testid={`care-verify-payment-${p.reference}`}>
        Verify payment · {p.status}
      </Button>
    ))}
  </article>
);

export const CareBillsView = () => {
  const { patientMode, api } = useCare();
  const { data, error, refresh } = useLiveCare('/bills', { bills: [] });
  const { data: quotes, refresh: reloadQuotes } = useLiveCare('/quotes', { quotes: [] });
  const { data: payments } = useLiveCare('/payments', { payments: [] });
  const [answering, setAnswering] = useState(null);
  const [note, setNote] = useState('');
  const [amounts, setAmounts] = useState({});
  const [dates, setDates] = useState({});
  const [busy, setBusy] = useState(false);

  const run = async operation => {
    setBusy(true);
    try { await operation(); refresh(); reloadQuotes(); } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };

  const answer = (quote, action) => run(async () => {
    await api.post(`/quotes/${quote.quote_id}/answer`, {
      action,
      note: note || (action === 'approve' ? 'I approve this exact quote.' : 'I decline this quote.'),
    });
    setAnswering(null);
    setNote('');
  });

  const pay = bill => run(async () => {
    const response = await api.post(`/bills/${bill.bill_id}/pay`, {
      amount_minor: Math.round(Number(amounts[bill.bill_id] || bill.remaining_minor / 100) * 100),
      idempotency_key: crypto.randomUUID(),
    });
    if (response.data.authorization_url) window.location.assign(response.data.authorization_url);
  });

  const verifyPayment = payment => run(() => api.post(`/payments/${payment.reference}/verify`));

  return (
    <div className="space-y-6">
      {error && <p role="alert" data-testid="care-bills-error">{error}</p>}
      <h2>Quotes and balances</h2>
      {quotes.quotes.map(q => (
        <QuoteCard
          key={q.quote_id}
          q={q}
          patientMode={patientMode}
          answering={answering}
          note={note}
          busy={busy}
          onOpenAnswer={(id) => { setAnswering(id); setNote(''); }}
          onNoteChange={setNote}
          onAnswer={answer}
        />
      ))}
      {!data.bills.length && <p className="care-empty" data-testid="care-bills-empty">No bills yet. A current patient-approved quote is required.</p>}
      {data.bills.map(b => (
        <BillCard
          key={b.bill_id}
          b={b}
          patientMode={patientMode}
          busy={busy}
          amount={amounts[b.bill_id]}
          date={dates[b.bill_id]}
          onAmountChange={(v) => setAmounts({ ...amounts, [b.bill_id]: v })}
          onDateChange={(v) => setDates({ ...dates, [b.bill_id]: v })}
          onPay={() => pay(b)}
          onLayaway={() => run(() => api.post(`/bills/${b.bill_id}/layaway`, { due_at: new Date(dates[b.bill_id]).toISOString() }))}
          onCancelRequest={() => run(() => api.post(`/bills/${b.bill_id}/resolution`, { action: 'request_cancellation' }))}
          onAgreeDueDate={() => run(() => api.post(`/bills/${b.bill_id}/agree-due-date`))}
          onRecheck={() => run(() => api.post(`/bills/${b.bill_id}/resolution`, { action: 'recheck' }))}
          onRefresh={refresh}
          onVerifyPayment={verifyPayment}
          payments={payments.payments}
        />
      ))}
    </div>
  );
};
